/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChecklistDataMap } from './checkbox-list-model';
import { CheckboxListModelService, Side } from './checkbox-list-model.service';

@Component({
	selector: 'checkbox-list',
	templateUrl: './checkbox-list.component.html',
	styleUrls: ['./checkbox-list.component.scss'],
	providers: [
		// Each <checkbox-list> instance is given its own CheckboxListModelService object instance,
		// so this service class is NOT a singleton.  That's because the "checkboxListModel"
		// holds data (especially the "currentMapOfValues") that is specific to the list and
		// the list's subcomponents.
		//
		CheckboxListModelService,
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: CheckboxListComponent,
			multi: true,
		},
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxListComponent implements ControlValueAccessor, OnInit, OnDestroy {
	private onChange: (value: any) => void;
	private _onTouched: () => void = () => {};

	private shutdownSubject$: Subject<boolean> = new Subject();

	constructor(private checkboxListModel: CheckboxListModelService) {}

	public ngOnInit(): void {
		this.checkboxListModel.modelUpdatedForExternalUse$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(() => {
				if (this.onChange) {
					this.onChange(this.checkboxListModel.currentMapOfValues);
				}
				//every external intended interaction is a user interaction and with a checkbox list
				//every relevant control interaction _is_ a change, soo... any change is a touch
				this._onTouched();
			});
	}

	public get checkboxSide(): Side {
		return this.checkboxListModel.checkboxSide;
	}

	@Input() public set checkboxSide(side: Side) {
		if (side !== 'left' && side !== 'right') {
			throw new Error('The [checkboxSide] attribute\'s value must be either "left" or "right"');
		}
		this.checkboxListModel.checkboxSide = side;
	}

	public get currentArrayOfValues() {
		return this.checkboxListModel.currentArrayOfValues;
	}

	public get currentMapOfValues() {
		return this.checkboxListModel.currentMapOfValues;
	}

	public writeValue(value: ChecklistDataMap | null): void {
		//the service does not support nulls and always assumes there's a map
		//it's... questionable if that was a wise choice. But for now, cooerce
		//null's to an empty map...
		if (!value) {
			value = new Map();
		}
		this.checkboxListModel.currentMapOfValues = value;
	}

	public registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	public registerOnTouched(fn: any): void {
		this._onTouched = fn;
	}

	public setDisabledState?(isDisabled: boolean): void {
		this.checkboxListModel.setDisabledState(isDisabled);
	}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next(true);
		this.shutdownSubject$.unsubscribe();
	}
}
